import { Link } from 'react-router-dom';

function Terms() {
  return (
    <div className="min-h-screen bg-puzzle-surface">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <h1 className="text-3xl font-bold text-puzzle-text mb-2">Terms of Service</h1>
        <p className="text-puzzle-text-secondary mb-8">Effective Date: January 2026</p>

        {/* Table of Contents */}
        <nav className="mb-10 p-4 bg-puzzle-background rounded-lg">
          <h2 className="font-semibold text-puzzle-text mb-3">Contents</h2>
          <ol className="list-decimal list-inside space-y-1 text-puzzle-blue text-sm">
            <li><a href="#acceptance" className="hover:underline">Acceptance of Terms</a></li>
            <li><a href="#service" className="hover:underline">Description of Service</a></li>
            <li><a href="#accounts" className="hover:underline">User Accounts</a></li>
            <li><a href="#conduct" className="hover:underline">User Conduct</a></li>
            <li><a href="#ip" className="hover:underline">Intellectual Property</a></li>
            <li><a href="#content" className="hover:underline">User Content</a></li>
            <li><a href="#disclaimers" className="hover:underline">Disclaimers</a></li>
            <li><a href="#liability" className="hover:underline">Limitation of Liability</a></li>
            <li><a href="#termination" className="hover:underline">Termination</a></li>
            <li><a href="#changes" className="hover:underline">Changes to Terms</a></li>
          </ol>
        </nav>

        {/* Content */}
        <div className="space-y-8 text-puzzle-text">
          <section id="acceptance" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using Puzzlists, you agree to be bound by these Terms of Service.
              If you do not agree to these terms, please do not use our service.
            </p>
          </section>

          <section id="service" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">2. Description of Service</h2>
            <p className="mb-3">
              Puzzlists is a free online jigsaw puzzle game that provides:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>Daily featured puzzles</li>
              <li>Custom puzzle creation from your own images</li>
              <li>Progress tracking and statistics</li>
              <li>Optional user accounts for cross-device sync</li>
            </ul>
          </section>

          <section id="accounts" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">3. User Accounts</h2>
            <p className="mb-3">
              Account registration is optional. If you choose to create an account:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>You must provide a valid email address and unique username</li>
              <li>You are responsible for maintaining the security of your account</li>
              <li>You may only maintain one account per person</li>
              <li>You must be at least 13 years old to create an account</li>
            </ul>
            <p className="mt-3">
              Guest users can play without registration, with progress saved locally
              in your browser.
            </p>
          </section>

          <section id="conduct" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">4. User Conduct</h2>
            <p className="mb-3">
              You agree not to:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>Use automated scripts, bots, or scrapers to access the service</li>
              <li>Attempt to reverse engineer or decompile any part of the service</li>
              <li>Interfere with or disrupt the service or servers</li>
              <li>Violate any applicable laws while using the service</li>
              <li>Impersonate others or misrepresent your identity</li>
            </ul>
          </section>

          <section id="ip" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">5. Intellectual Property</h2>
            <p className="mb-3">
              The Puzzlists name, logo, and service design are owned by Puzzlists.
              You may not use our branding without permission.
            </p>
            <p>
              Images used in daily puzzles are curated by our team. User-uploaded images
              remain the property of the user who uploaded them.
            </p>
          </section>

          <section id="content" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">6. User Content</h2>
            <p className="mb-3">
              When you upload images to create custom puzzles:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>You retain ownership of your images</li>
              <li>You grant us a license to display the image as part of the puzzle experience</li>
              <li>You confirm you have the right to use and share the image</li>
              <li>You agree not to upload illegal, offensive, or inappropriate content</li>
            </ul>
            <p className="mt-3">
              We reserve the right to remove any content that violates these terms.
            </p>
          </section>

          <section id="disclaimers" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">7. Disclaimers</h2>
            <p>
              Puzzlists is provided &quot;as is&quot; without warranties of any kind. We do not
              guarantee that the service will be available at all times, error-free, or
              secure. We make no warranties about the accuracy or reliability of the service.
            </p>
          </section>

          <section id="liability" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">8. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Puzzlists shall not be liable for
              any indirect, incidental, special, or consequential damages, including but
              not limited to loss of data, loss of progress, or service interruptions.
            </p>
          </section>

          <section id="termination" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">9. Termination</h2>
            <p className="mb-3">
              We may suspend or terminate your access to the service at any time for
              violation of these terms or for any other reason at our discretion.
            </p>
            <p>
              You may delete your account at any time through your
              {' '}
              <Link to="/account" className="text-puzzle-blue hover:underline">Account Settings</Link>
              . Upon deletion, your account data will be permanently removed.
            </p>
          </section>

          <section id="changes" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">10. Changes to Terms</h2>
            <p>
              We may update these Terms of Service from time to time. When we make
              significant changes, we will update the effective date at the top of this page.
              Continued use of the service after changes constitutes acceptance of the new terms.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-puzzle-border">
          <p className="text-puzzle-text-secondary text-sm">
            See also our
            {' '}
            <Link to="/privacy" className="text-puzzle-blue hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Terms;
